import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';
import { permissions } from '../../permissions';
import * as _ from 'lodash';

import { Page } from '../../model/page';
import { TableExtensionService } from '../oes/table-extension.service';
import { VendorService } from '../../services/vendor.service';
import { GridFiltersService } from '../../services/grid.filters.service';
import { Vendor } from '../../model/vendor';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs/Subscription';
import { ConfirmationService } from 'primeng/api';
import { NgxPermissionsService } from 'ngx-permissions';
import { AuthService } from '../../auth/auth.service';

@Component({
  templateUrl: 'vendors.component.html',
  providers: [GridFiltersService],
})
export class VendorsComponent implements OnInit, OnDestroy {
  @ViewChild('actionbar') actionbar: ElementRef;
  @ViewChild('tableHeader') tableHeader: ElementRef;
  @ViewChild('tableFilter') tableFilter: ElementRef;
  @ViewChild('paginator') paginator: ElementRef;

  public loading = false;
  public permissions = permissions;
  private companies: Array<any> = [];
  public selected: Array<any>;
  public viewFilters: boolean;
  public scrollHeight = '75px';
  private subscriptions: Array<Subscription>;

  page = new Page();
  rows = new Array<Vendor>();
  cols = [];
  totalRecords = 0;

  private filters: { [key: string]: string } = {};
  private hasVendorEdit = false;
  private hasVendorAccess = false;
  isSuperAdmin = false;
  canCreateTradingPartner = false;

  constructor(
    private router: Router,
    private vendorService: VendorService,
    private toastr: ToastrService,
    private gridFiltersService: GridFiltersService,
    private confirmationService: ConfirmationService,
    private title: Title,
    private tableExtensionService: TableExtensionService,
    private permissionsService: NgxPermissionsService,
    private authService: AuthService
  ) {
    this.page.pageNumber = 0;
    this.page.size = 50;
    this.title.setTitle('EDI - Vendors');
  }

  ngOnInit() {
    this.viewFilters = true;
    this.selected = [];
    this.subscriptions = [];
    this.loading = true;
    this.cols = [
      { field: 'VD1_ID', header: 'ID', visible: true, width: 50, sort: false },
      {
        field: 'CO1_ID',
        header: 'Company',
        visible: true,
        width: 80,
        sort: false,
      },
      {
        field: 'VD1_NAME',
        header: 'Vendor Name',
        visible: true,
        width: 100,
        sort: false,
      },
      {
        field: 'VENDOR_ID',
        header: 'Vendor ID',
        visible: true,
        width: 80,
        sort: false,
      },
      {
        field: 'VD1_SHOW_DEFAULT',
        header: 'Active',
        visible: true,
        width: 60,
        sort: false,
      },
      {
        field: 'VD1_TEST_MODE',
        header: 'Test Mode',
        visible: true,
        width: 60,
        sort: false,
      },
      {
        field: 'VD1_SEND_RECEIVE_EDI',
        header: 'Send/Receive EDI',
        visible: true,
        width: 100,
        sort: false,
      },
      {
        field: 'VD1_FILTERS',
        header: 'Connection Method',
        visible: true,
        width: 100,
        sort: false,
      },
      {
        field: 'VD1_MODIFIED_ON',
        header: 'Modified On',
        visible: true,
        width: 100,
        sort: false,
      },
      { field: 'VD1_MAP', header: 'Map', visible: true, width: 80, sort: true },
    ];
    this.page.filterParams = _.clone(this.filters);
    this.subscriptions.push(
      this.vendorService.preloadData().subscribe((data) => {
        this.prepareData(data);
        this.setPage({ offset: 0 });
      })
    );

    this.permissionsService
      .hasPermission(this.permissions.VENDORS_EDIT)
      .then((hasPermit) => {
        this.hasVendorEdit = hasPermit;
      });

    this.permissionsService
      .hasPermission(this.permissions.VENDORS_ACCESS)
      .then((hasPermit) => {
        this.hasVendorAccess = hasPermit;
      });

    this.permissionsService
      .hasPermission([this.permissions.ADMIN])
      .then((hasPermit) => {
        this.isSuperAdmin = hasPermit;
      });

    this.permissionsService
        .hasPermission([this.permissions.EDI_TRADING_PARTNER_ADD])
        .then((hasPermit) => {this.canCreateTradingPartner = hasPermit;
      });
  }

  ngOnDestroy() {
    for (let index = 0; index < this.subscriptions.length; index++) {
      if (this.subscriptions[index]) {
        this.subscriptions[index].unsubscribe();
      }
    }
  }

  prepareData(data) {
    this.companies = data.companies.map((company) => {
      return {
        CO1_ID: company.CO1_ID,
        name: 'CO1_ID:' + company.CO1_ID,
        text: company.CO1_NAME,
      };
    });
  }

  getData() {
    this.loading = true;
    this.page.filterParams = _.clone(this.filters);
    this.setPage({ offset: this.page.pageNumber });
  }

  setPage(pageInfo) {
    this.page.pageNumber = pageInfo.offset;
    this.selected = [];
    this.subscriptions.push(
      this.serverCall(this.page).subscribe(
        (result) => {
          this.rows = result.data.map((vendor) => {
            vendor['id'] = vendor['VD1_ID'] + '_' + vendor['CO1_ID'];
            return vendor;
          });
          this.totalRecords = result.count;
          //window.dispatchEvent(new Event("resize"));
          this.scrollHeight = this.getScrollHeight() + 'px';
          this.loading = false;
        },
        (error) => {
          this.loading = false;
          window.dispatchEvent(new Event('resize'));
          this.toastr.error('Request timed out.', 'Service Error', {
            timeOut: 3000,
          });
        }
      )
    );
  }

  onColumnResize(event) {
    this.tableExtensionService.onColumnResize(this.cols, event);
  }

  serverCall(page: Page): Observable<any> {
    return this.vendorService.getVendors(this.page);
  }

  editVendor(record): void {
    this.router.navigate(['vendors/edit/' + record.VD1_ID + '/' + record.CO1_ID]);
  }

  addVendor(): void {
    this.router.navigate(['vendors/add/0/' + this.authService.companyId]);
  }

  onPage(event) {
    this.page.size = event.rows;
    this.setPage({
      offset: event.first / event.rows,
    });
  }

  getScrollHeight(): number {
    return (
        this.getContentDialogHeight() -
        (this.tableHeader.nativeElement.offsetHeight +
        (this.paginator.nativeElement.offsetHeight + 55) +
        (this.viewFilters && this.tableFilter
            ? this.tableFilter.nativeElement.offsetHeight
            : 0))
    );
  }

  getContentDialogHeight(): number {
    return (
        window.innerHeight - (this.actionbar.nativeElement.offsetHeight + 48) - 77
    );
  }

  onDelete() {
    if (this.selected.length > 0) {
      this.confirmationService.confirm({
        header: 'Delete records',
        message:
          'Are you sure to delete selected ' +
          this.selected.length +
          ' ' +
          this.appendPlural('record', 's', this.selected.length) +
          '?',
        accept: () => {
          this.loading = true;
          const vendors = this.selected.map((vendor) => {
            return {
              VD1_ID: vendor['VD1_ID'],
              CO1_ID: vendor['CO1_ID'],
            };
          });
          this.vendorService.deleteMultiple(vendors).subscribe(
            (result) => {
              if (
                result.hasOwnProperty('successCount') &&
                result.hasOwnProperty('errorCount')
              ) {
                const deleted = Number(result['successCount']);
                const failed = Number(result['errorCount']);
                this.toastr.info(
                  'Deleted ' +
                    deleted +
                    ' ' +
                    this.appendPlural('record', 's', deleted) +
                    '.' +
                    (failed > 0
                      ? ' Failed to delete ' +
                        failed +
                        ' ' +
                        this.appendPlural('record', 's', failed) +
                        '.'
                      : '')
                );
                this.getData();
              } else {
                this.toastr.error('Unexpected error encountered.');
              }
              this.loading = false;
            },
            () => {
              this.loading = false;
              this.toastr.error('Request timed out.', 'Service Error');
            }
          );
        },
      });
    }
  }

  private appendPlural(
    baseString: string,
    postFix: string,
    value: Number
  ): string {
    return baseString + (value > 1 ? postFix : '');
  }

  onSort(col) {
    if (col.sort) {
      if (this.filters.hasOwnProperty('SORT_COLUMN')) {
        if (this.filters['SORT_COLUMN'] === col.field) {
          if (this.filters['SORT_DIRECTION'] === 'ASC') {
            this.filters['SORT_DIRECTION'] = 'DESC';
          } else {
            this.filters['SORT_DIRECTION'] = 'ASC';
          }
        } else {
          this.filters['SORT_COLUMN'] = col.field;
          this.filters['SORT_DIRECTION'] = 'ASC';
        }
      } else {
        this.filters['SORT_COLUMN'] = col.field;
        this.filters['SORT_DIRECTION'] = 'ASC';
      }
      this.loading = true;
      this.page.pageNumber = 0;
      this.getData();
    }
  }

  onSubmit() {
    const filters = this.gridFiltersService.prepare(this.filters);

    let reload = false;
    const keys = Object.keys(filters).concat(
      Object.keys(this.page.filterParams)
    );
    for (const key of keys) {
      if (this.page.filterParams[key] !== filters[key]) {
        reload = true;
        break;
      }
    }

    if (reload) {
      this.page.filterParams = filters;
      this.getData();
    }
  }

  toggleFilterView() {
    const filters = _.clone(this.filters);
    let validFilterPresent = false;
    if (Object.keys(filters).length > 0) {
      for (const key of Object.keys(filters)) {
        if (filters[key] && key !== 'SORT_COLUMN' && key !== 'SORT_DIRECTION') {
          validFilterPresent = true;
        }
      }
    }
    if (!validFilterPresent) {
      this.viewFilters = !this.viewFilters;
      this.onWindowResize();
    }
  }

  cellDoubleClick(record) {
    if (this.hasVendorEdit) {
      this.editVendor(record);
    }
  }

  onWindowResize() {
    setTimeout(() => {
      this.scrollHeight = this.getScrollHeight() + 'px';
      window.dispatchEvent(new Event('resize'));
    }, 150);
  }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/vendors/vendors.component.ts